/**
 * Return-URL validation for the sign-in round trip.
 *
 * ONE mechanism now, not two. Both sign-in paths carry the destination the
 * same way: `SITE_URL + next`, embedded directly in the redirect target —
 * OAuth's `redirectTo` (client-built) and the magic link's `emailRedirectTo`
 * (server-built, by `supabase/functions/otp-request`, which ports this exact
 * rule set). The previous localStorage fallback is gone entirely, not kept
 * as a backstop: a magic link opened in a mobile mail app's in-app webview is
 * a DIFFERENT storage context, so localStorage was never actually reachable
 * on the one path that needed it most, and two mechanisms answering "where
 * was this rep going" is the same objection as the withdrawn nullable
 * `order_lines.rep_id` hedge — the one that survives fewer contexts wins
 * silently. The link carries the destination or nothing does.
 *
 * `safeReturnPath` here MUST match `isValidNextPath` in
 * supabase/functions/otp-request/index.ts rule for rule. That function is
 * the actual auth-boundary enforcement (this app cannot bypass it by
 * constructing an invalid link some other way); this copy exists so the
 * client fails the same request the same way, before spending a network
 * round trip on something the server would reject anyway, and so the OAuth
 * path — which never goes through otp-request — gets the identical guard.
 */

const NEXT_MAX_LEN = 256
const NEXT_ALLOWED_PREFIXES = ['/sku/', '/orders/']
/** Control chars (0x00-0x1F, 0x7F) plus every Unicode whitespace \s matches
 *  — including NBSP (U+00A0). See #^btrim-strips-spaces-only. */
const UNSAFE_CHARS = /[\x00-\x1F\x7F\s]/

/**
 * Reduce an arbitrary string to a validated same-origin path, or null.
 *
 * This is an OPEN REDIRECT guard on the auth boundary, not tidying. Reject,
 * never decode-then-revalidate: none of the three allowed routes needs a
 * `%`-encoded character (SKUs and order numbers are alphanumeric), and
 * decode-then-validate is where open-redirect bypasses live.
 */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (raw.length === 0 || raw.length > NEXT_MAX_LEN) return null
  if (raw[0] !== '/') return null
  // Second character not `/` or `\` — blocks protocol-relative
  // (`//evil.example`) and its backslash variant.
  if (raw.length > 1 && (raw[1] === '/' || raw[1] === '\\')) return null
  if (raw.includes(':')) return null
  if (raw.includes('\\')) return null
  if (raw.includes('%')) return null
  if (raw.includes('#')) return null
  if (raw.includes('..')) return null
  if (UNSAFE_CHARS.test(raw)) return null

  if (raw === '/') return raw
  return NEXT_ALLOWED_PREFIXES.some((prefix) => raw.startsWith(prefix)) ? raw : null
}

/** Where the user actually is, as a path we can return them to. */
export function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`
}

/**
 * Resolve the destination after a successful sign-in. `next` — read from a
 * query param on the (now legacy, rarely hit) `/auth/callback?next=...`
 * shape — wins when present; `/` is the floor. There is no storage fallback
 * to fall further to.
 */
export function resolveReturnPath(nextParam: string | null): string {
  return safeReturnPath(nextParam) ?? '/'
}
